import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { preloadCharacterAssets, preloadNumberTextures, preloadTeamTextures, createPlayerObject } from './utilitiesFootball3D';

// Serialize shared cache population when appearance changes arrive during a load.
let loads = Promise.resolve();
export function createPreview(container, options = {}) {
  if (options.resolveAsset) THREE.DefaultLoadingManager.setURLModifier(options.resolveAsset);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.setAttribute('aria-label', 'Animated full-body character preview. Drag to rotate.');
    container.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#d8e0e8');
    scene.add(new THREE.HemisphereLight(0xe7f4ff, 0x77705e, 2));
    const key = new THREE.DirectionalLight(0xfff0df, 3);key.position.set(3, 5, 4);scene.add(key);
    const fill = new THREE.DirectionalLight(0xc5dcff, 1.2);fill.position.set(-4, 3, -2);scene.add(fill);
    const camera = new THREE.PerspectiveCamera(35, 1, .01, 50);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 1.02, 0);
    controls.enablePan = false;controls.enableZoom = false;
    controls.minPolarAngle = .65;controls.maxPolarAngle = 1.8;
    let player, destroyed = false, generation = 0;
    const timer = new THREE.Timer();
    // The last size we drew at. Without this the observer feeds itself: the
    // container has a 1px border, getBoundingClientRect() reports the BORDER
    // box, and a canvas sized to that is 2px wider than the content box — so
    // every resize grew the container, which queued another resize, which the
    // browser aborted with "ResizeObserver loop completed with undelivered
    // notifications" (a red error screen in dev, a silent stall in production).
    let lastW = 0, lastH = 0;
    function resize() {
        // clientWidth/clientHeight = the CONTENT box: the canvas can never be
        // wider than the space the layout gave it.
        const width = container.clientWidth, height = container.clientHeight;
        if (!width || !height) return;
        if (width === lastW && height === lastH) return;
        lastW = width; lastH = height;
        renderer.setSize(width, height, true);camera.aspect = width / height;
        // Fit the full body even in a narrow portrait modal column.
        const distance = Math.max(4.1, 1.3 / (Math.tan(THREE.MathUtils.degToRad(17.5)) * Math.min(1, camera.aspect)));
        const direction = camera.position.clone().sub(controls.target);
        if (direction.length() < .1) direction.set(.25, .08, 1);
        camera.position.copy(controls.target).add(direction.normalize().multiplyScalar(distance));
        camera.updateProjectionMatrix();controls.update();
    }
    camera.position.set(1.1, 1.35, 4.3);
    // Deferred to the next frame: a ResizeObserver callback that changes layout
    // must not do it inside the observer's own delivery pass.
    let resizeQueued = false;
    const observer = new ResizeObserver(() => {
        if (resizeQueued || destroyed) return;
        resizeQueued = true;
        requestAnimationFrame(() => { resizeQueued = false; if (!destroyed) resize(); });
    });
    observer.observe(container);resize();
    function clearPlayer() {
        if (!player) return;
        scene.remove(player);player.userData.mixer?.stopAllAction();
        player.userData.mixer?.uncacheRoot(player);
        const disposed = new Set();
        player.traverse(o => {
            if (o.isSkinnedMesh && o.skeleton && !disposed.has(o.skeleton)) { disposed.add(o.skeleton);o.skeleton.dispose(); }
            if (o.isSprite) { o.material.map?.dispose();o.material.dispose(); }
            // Mesh geometry and image textures belong to the reusable asset cache.
            else if (o.isMesh) for (const mat of (Array.isArray(o.material) ? o.material : [o.material])) {
                if (!disposed.has(mat)) { disposed.add(mat);mat.dispose(); }
            }
        });
        player = null;
    }
    renderer.setAnimationLoop(() => {
        if (destroyed) return;
        timer.update();const dt = Math.min(timer.getDelta(), .05);
        player?.userData.mixer?.update(dt);controls.update();renderer.render(scene, camera);
    });
    return {
        async update(look) {
            const version = ++generation;
            const load = async () => {
                if (destroyed || version !== generation) return;
                await preloadCharacterAssets('', { neededSkinStyles: [look.skinStyle], neededHairStyles: [look.hairStyle] });
                await preloadNumberTextures('', { playerInfo: [{ number: look.number }] });
                await preloadTeamTextures(look.teamTextures);
            };
            const pending = loads.then(load, load);loads = pending.catch(() => {});await pending;
            if (destroyed || version !== generation) return;
            clearPlayer();
            player = createPlayerObject({ name: look.name, number: look.number }, {
                skinStyle: look.skinStyle, hairStyle: look.hairStyle, side: 'home',
                isGK: /^gk$/i.test(look.position || ''), teamTextures: look.teamTextures, numberColor: look.numberColor,
            });
            player.traverse(o => { if (o.isSprite) o.visible = false; });
            scene.add(player);
            const mixer = player.userData.mixer;
            const idle = player.userData.clips?.find(c => c.name === 'pose_stand');
            if (mixer && idle) { mixer.stopAllAction();mixer.clipAction(idle).reset().setLoop(THREE.LoopRepeat, Infinity).play();mixer.update(0); }
        },
        destroy() {
            destroyed = true;generation++;renderer.setAnimationLoop(null);observer.disconnect();
            clearPlayer();controls.dispose();timer.dispose();renderer.dispose();renderer.forceContextLoss();renderer.domElement.remove();
        },
    };
}
